<template>
  <div class="page-container reveal">
    <el-card class="main-list-card">
      <div class="list-header">
        <div class="header-content">
          <h2 class="page-title">Service Après-Vente (SAV)</h2>
          <p class="page-subtitle">{{ tickets.length }} ticket{{ tickets.length !== 1 ? 's' : '' }} en base de données</p>
        </div>
        <div class="header-actions">
          <el-input
            v-model="searchQ"
            placeholder="Nom, téléphone, motif..."
            class="w-64"
            @input="loadTickets"
          >
            <template #prefix><el-icon><Search /></el-icon></template>
          </el-input>
          
          <el-select v-model="statusFilter" class="w-48" @change="loadTickets">
            <el-option label="Tous les statuts" value="" />
            <el-option label="Nouveau (Ouvert)" value="open" />
            <el-option label="En cours de traitement" value="in_progress" />
            <el-option label="Problème Résolu" value="resolved" />
            <el-option label="Fermé sans suite" value="closed" />
          </el-select>

          <el-button @click="loadTickets" :loading="isLoading" circle :icon="Refresh" />
        </div>
      </div>

      <div v-if="selectedRows.length && canResolveSav" class="bulk-bar">
        <span class="bulk-count">{{ selectedRows.length }} sélectionné(s)</span>
        <el-button type="primary" size="small" :loading="bulkLoading" @click="bulkStatus('in_progress')">Prendre en charge</el-button>
        <el-button type="success" size="small" :loading="bulkLoading" @click="bulkStatus('resolved')">Marquer résolu</el-button>
        <el-button type="danger" size="small" :loading="bulkLoading" @click="bulkStatus('closed')">Fermer sans suite</el-button>
      </div>

      <el-table :data="paged" v-loading="isLoading" class="custom-table" stripe @selection-change="onSelectionChange">
        <el-table-column type="selection" width="45" />
        <el-table-column label="Réf. Ticket" width="100">
          <template #default="{ $index }">
            <span class="font-display font-bold text-profissa-text-muted">#{{ String($index + 1 + (page - 1) * pageSize).padStart(3, '0') }}</span>
          </template>
        </el-table-column>
        <el-table-column label="Ouvert le" width="160" sortable :sort-method="(a, b) => new Date(a.created_at) - new Date(b.created_at)">
          <template #default="{ row }">
            <span class="text-xs text-profissa-text-muted">{{ fmtDate(row.created_at) }}</span>
          </template>
        </el-table-column>
        <el-table-column label="Description du Problème" min-width="260">
          <template #default="{ row }">
            <span class="text-sm">{{ truncate(row.description, 65) }}</span>
          </template>
        </el-table-column>
        <el-table-column label="État d'avancement" width="160" sortable :sort-method="(a, b) => (a.status || '').localeCompare(b.status || '')">
          <template #default="{ row }">
            <div class="px-3 py-1 rounded-full text-[0.7rem] font-bold uppercase tracking-wide inline-block"
                 :class="{
                   'bg-orange-500/10 text-orange-500 border border-orange-500/20': row.status === 'open',
                   'bg-blue-500/10 text-blue-500 border border-blue-500/20': row.status === 'in_progress',
                   'bg-green-500/10 text-green-500 border border-green-500/20': row.status === 'resolved',
                   'bg-white/5 text-profissa-text-dim border border-profissa-glass-border': row.status === 'closed'
                 }">
              {{ statusLabel(row.status) }}
            </div>
          </template>
        </el-table-column>
        <el-table-column label="Gérer" width="80" fixed="right" align="center">
          <template #default="{ row }">
            <ActionDropdown :actions="getActions(row)" @action="(key) => onAction(key, row)" />
          </template>
        </el-table-column>
      </el-table>

      <div class="flex justify-end pt-6 mt-4 border-t border-profissa-glass-border">
        <el-pagination
          v-if="tickets.length > pageSize"
          v-model:current-page="page"
          v-model:page-size="pageSize"
          layout="total, prev, pager, next"
          :total="tickets.length"
          @size-change="page = 1"
        />
      </div>
    </el-card>

    <el-dialog append-to-body
      v-model="showDetail"
      :title="`Dossier SAV — ${selectedTicket ? fmtDate(selectedTicket.created_at) : ''}`"
      width="600px"
      custom-class="glass-dialog"
    >
      <template v-if="selectedTicket">
        <div class="grid grid-cols-2 gap-4 mb-6">
          <div class="flex flex-col gap-1">
            <span class="text-xs font-bold text-profissa-text-muted uppercase tracking-wider">État actuel du dossier</span>
            <div>
              <div class="px-3 py-1 rounded-full text-[0.7rem] font-bold uppercase tracking-wide inline-block"
                   :class="{
                     'bg-orange-500/10 text-orange-500 border border-orange-500/20': selectedTicket.status === 'open',
                     'bg-blue-500/10 text-blue-500 border border-blue-500/20': selectedTicket.status === 'in_progress',
                     'bg-green-500/10 text-green-500 border border-green-500/20': selectedTicket.status === 'resolved',
                     'bg-white/5 text-profissa-text-dim border border-profissa-glass-border': selectedTicket.status === 'closed'
                   }">
                {{ statusLabel(selectedTicket.status) }}
              </div>
            </div>
          </div>
          <div class="flex flex-col gap-1">
            <span class="text-xs font-bold text-profissa-text-muted uppercase tracking-wider">Date d'enregistrement</span>
            <span class="font-bold font-mono">{{ fmtDate(selectedTicket.created_at) }}</span>
          </div>
          <div class="col-span-2 flex flex-col gap-1 mt-2">
            <span class="text-xs font-bold text-profissa-text-muted uppercase tracking-wider">Plainte / Description détaillée</span>
            <div class="p-4 rounded-xl bg-white/5 border border-profissa-glass-border text-sm leading-relaxed whitespace-pre-wrap">
              {{ selectedTicket.description }}
            </div>
          </div>
        </div>

        <div v-if="selectedTicket.photo_urls?.length" class="mb-6">
          <span class="text-xs font-bold text-profissa-text-muted uppercase tracking-wider mb-2 block">Preuves photographiques</span>
          <div class="flex flex-wrap gap-2">
            <a v-for="(url, i) in selectedTicket.photo_urls" :key="i" :href="url" target="_blank" class="block overflow-hidden rounded-lg border border-profissa-glass-border hover:border-profissa-accent transition-colors">
              <el-image :src="url" :alt="'Photo '+(i+1)" class="w-24 h-24 object-cover" :preview-src-list="selectedTicket.photo_urls" :initial-index="i" />
            </a>
          </div>
        </div>

        <div v-if="['resolved','closed'].includes(selectedTicket.status)" class="mb-4">
          <el-alert type="success" title="Ce ticket a été traité et est définitivement clôturé." :closable="false" class="bg-green-500/10 border border-green-500/20 text-green-500 font-bold" />
        </div>
      </template>

      <template #footer>
        <div class="flex justify-between items-center w-full">
          <div class="flex gap-2">
            <template v-if="selectedTicket && !['resolved','closed'].includes(selectedTicket.status) && canResolveSav">
              <el-button
                v-if="selectedTicket.status === 'open'"
                type="primary"
                :loading="isResolving"
                @click="resolve('in_progress')"
                :icon="RefreshRight"
              >
                Prendre en charge
              </el-button>
              <el-button type="success" :loading="isResolving" @click="resolve('resolved')" :icon="Check">
                Clôturer (Problème Réglé)
              </el-button>
              <el-button type="danger" :loading="isResolving" @click="resolve('closed')" :icon="CircleClose">
                Fermer sans suite
              </el-button>
            </template>
          </div>
          <el-button @click="showDetail = false">Fermer la vue</el-button>
        </div>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { onMounted, ref, computed } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import { Search, Refresh, View, RefreshRight, Check, CircleClose } from '@element-plus/icons-vue'
import { extractApiError } from '../api'
import { listSavTickets, resolveSavTicket } from '../services/adminApi'
import ActionDropdown from './shared/ActionDropdown.vue'
import { useAdminPermissions } from '../composables/useAdminPermissions'

const selectedRows   = ref([])
const tickets        = ref([])
const isLoading      = ref(false)
const searchQ        = ref('')
const statusFilter   = ref('')
const selectedTicket = ref(null)
const showDetail     = ref(false)
const isResolving    = ref(false)
const page           = ref(1)
const pageSize       = ref(15)
const { hasPermission } = useAdminPermissions()
const canResolveSav = computed(() => hasPermission('sav.resolve.any'))

const paged = computed(() => tickets.value.slice((page.value - 1) * pageSize.value, page.value * pageSize.value))
const onSelectionChange = (rows) => { selectedRows.value = rows }
const bulkLoading = ref(false)

const bulkStatus = async (newStatus) => {
  if (!canResolveSav.value) return
  const labels = { in_progress: 'traitement', resolved: 'résolu', closed: 'clôturé' }
  const targets = selectedRows.value.filter(r => r.status !== newStatus && r.status !== 'resolved' && r.status !== 'closed')
  if (!targets.length) { ElMessage.warning('Aucun ticket éligible.'); return }
  try {
    await ElMessageBox.confirm(`Passer ${targets.length} ticket(s) en "${labels[newStatus]}" ?`, 'Action groupée', { type: 'warning' })
    bulkLoading.value = true
    let ok = 0
    for (const row of targets) { try { await resolveSavTicket(row.id, { status: newStatus }); ok++ } catch { /* skip */ } }
    ElMessage.success(`${ok} ticket(s) mis à jour.`)
    await loadTickets()
  } catch { /* cancelled */ }
  finally { bulkLoading.value = false }
}

const truncate = (v, n = 60) => String(v || '').length > n ? String(v).slice(0, n) + '…' : (v || '')
const fmtDate  = (d) => d ? new Date(d).toLocaleDateString('fr-FR', { day:'numeric', month:'short', year:'numeric', hour:'2-digit', minute:'2-digit' }) : '—'

const statusLabel   = (s) => ({ open: 'Nouveau', in_progress: 'En Traitement', resolved: 'Résolu', closed: 'Clôturé' }[s] || s || '—')

const loadTickets = async () => {
  isLoading.value = true
  try { tickets.value = await listSavTickets(searchQ.value, statusFilter.value); page.value = 1 }
  catch (e) { ElMessage.error(extractApiError(e)) }
  finally { isLoading.value = false }
}

const getActions = (row) => {
  const actions = [{ key: 'view', label: 'Examiner le dossier', icon: View }]
  if (canResolveSav.value && row.status === 'open') {
    actions.push({ key: 'in_progress', label: 'Démarrer Traitement', icon: RefreshRight, divided: true })
  }
  if (canResolveSav.value && row.status !== 'resolved' && row.status !== 'closed') {
    actions.push({ key: 'resolved', label: 'Marquer comme Résolu', icon: Check })
  }
  if (canResolveSav.value && row.status !== 'closed') {
    actions.push({ key: 'closed', label: 'Fermer Sans Suite', icon: CircleClose, danger: true })
  }
  return actions
}

const onAction = (key, row) => {
  if (key === 'view') {
    selectedTicket.value = row
    showDetail.value = true
  } else {
    if (!canResolveSav.value) return
    selectedTicket.value = row
    resolve(key)
  }
}

const resolve = async (newStatus) => {
  if (!canResolveSav.value) return
  isResolving.value = true
  try {
    await resolveSavTicket(selectedTicket.value.id, { status: newStatus })
    const labels = { in_progress: 'mis en traitement', resolved: 'déclaré résolu', closed: 'clôturé' }
    ElMessage.success(`Statut mis à jour : Ticket ${labels[newStatus] || newStatus}.`)
    showDetail.value = false
    await loadTickets()
  } catch (e) { ElMessage.error(extractApiError(e)) }
  finally { isResolving.value = false }
}

onMounted(loadTickets)
</script>

<style scoped>
.page-container { @apply h-full flex flex-col; }
.main-list-card { @apply flex-1 flex flex-col; }
.list-header { @apply flex justify-between items-end pb-8; }
.page-title { @apply text-2xl font-black mb-1; }
.page-subtitle { @apply text-profissa-text-muted text-sm font-semibold; }
.header-actions { @apply flex gap-3 items-center; }
.bulk-bar { @apply flex items-center gap-3 px-4 py-3 mb-4 rounded-xl; background: rgba(255, 107, 0, 0.06); border: 1px solid rgba(255, 107, 0, 0.15); }
.bulk-count { @apply font-extrabold text-sm text-profissa-accent mr-auto; }
</style>
